import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { AppModeProvider, useAppMode } from './AppModeContext';
import { CanvasSessionProvider, useCanvasSession } from './CanvasSessionContext';
import { useCourseData } from '../hooks/useCourseData';
import { useUserSwitching } from '../hooks/useUserSwitching';
import { testCanvasCredentials } from '../services/canvasService';
import { AuthUser, CanvasUser } from '../types/auth';

// Task 4.5: this file used to own session state (active user, available
// users, switching), mode state (guest/demo/authenticated and the
// credentials each implies) AND course-data fetching, in one 1,145-line
// file. Session and mode moved out to CanvasSessionContext.tsx and
// AppModeContext.tsx; the course-fetching logic (tasks 4.4a-4.4c) and the
// user-switching orchestration moved into two plain hooks (useCourseData,
// useUserSwitching) so this file could stay a thin composition layer under
// its line budget. See each file's own header comment for the reasoning
// behind where each piece landed and why the dependencies run the
// direction they do (AppModeContext -> CanvasSessionContext -> the hooks
// below, composed here).
//
// CanvasDataProvider is kept as a single component consumers still wrap
// their tree in, and useCanvasData() is kept as a working, if now largely
// deprecated, single-hook surface: currentUser/availableUsers/
// switchCanvasUser/userId and the mode/guest fields AppModeContext owns are
// passed through unchanged from the two new contexts so existing consumers
// keep compiling without changes. New/migrated consumers should prefer
// `useAppMode()` and `useCanvasSession()` directly for those fields.

// Task 5.5i: widened to cover every field the provider's `value` (below)
// actually supplies -- the previous nine-field version was stale (see the
// task report for how it drifted) and every consumer already read past it
// unchecked. Written out field-by-field rather than derived as `typeof
// value` because `value` is built inside CanvasDataInner from hook return
// values that only exist at render time; deriving it live would mean
// restructuring the provider, which risks the exact behaviour change this
// task is not allowed to make. `@deprecated` tags are copied from the
// `value` useMemo's own JSDoc comments below -- they're how a consumer
// still reads them once the compiler stops the fields being invisible.
interface CanvasDataContextType {
  currentUser: AuthUser | null;
  availableUsers: CanvasUser[];
  switchCanvasUser: (userId: string | number) => Promise<boolean>;
  userId: string | null;
  courses: any[];
  courseDetails: any;
  loadingCourses: boolean;
  loadingCourseDetails: boolean;
  loadingCourseHistory: boolean;
  error: string;
  /** @deprecated read this mode field from useAppMode() instead */
  isGuestMode: boolean;
  /** @deprecated read this mode field from useAppMode() instead */
  isDemoMode: boolean;
  /** @deprecated read this mode field from useAppMode() instead */
  isOfflineMode: boolean;
  /** @deprecated read this mode field from useAppMode() instead */
  initializing: boolean;
  /** @deprecated read this mode field from useAppMode() instead */
  pendingUserSelection: boolean;
  /** @deprecated read this mode field from useAppMode() instead */
  guestAvailableUsers: any[];
  loadCourses: () => Promise<void>;
  getCourse: (courseId: string | number) => Promise<any>;
  getCourseDetails: (courseId: string | number) => Promise<any>;
  getCourseHistory: (courseId: string | number) => Promise<any>;
  loadAllCourseDetails: (activeOnly?: boolean) => Promise<void>;
  validateCanvasToken: (
    token: string,
    userIdArg: string | null,
    rootUrl: string
  ) => Promise<any>;
  /** @deprecated read this mode field from useAppMode() instead */
  setGuestCredentials: (
    token: string,
    userId: string | number | null,
    rootUrl: string,
    users?: any[]
  ) => void;
  clearGuestCredentials: () => Promise<void>;
  /** @deprecated read this mode field from useAppMode() instead */
  getActiveCredentials: () => any;
  selectCanvasUser: (selectUserId: string | number, shouldNavigate?: boolean) => Promise<boolean>;
  switchGuestUser: (selectUserId: string | number, shouldNavigate?: boolean) => Promise<void>;
  resetCourseData: () => void;
  clearAllCanvasData: () => void;
  refreshAllData: () => Promise<boolean>;
  clearCache: () => void;
  /** @deprecated read this mode field from useAppMode() instead */
  activateDemoMode: () => Promise<boolean>;
  /** @deprecated read this mode field from useAppMode() instead */
  deactivateDemoMode: () => void;
}

const CanvasDataContext = createContext<CanvasDataContextType | undefined>(undefined);

// Custom hook to use the Canvas data context - MAKE SURE THIS IS EXPORTED
export const useCanvasData = (): CanvasDataContextType => {
  const context = useContext(CanvasDataContext);
  if (!context) {
    throw new Error('useCanvasData must be used within a CanvasDataProvider');
  }
  return context;
};

interface CanvasDataProviderProps {
  children: ReactNode;
}

// Composes AppModeContext + CanvasSessionContext + the two course-data
// hooks into the single value useCanvasData() exposes. Rendered inside
// AppModeProvider/CanvasSessionProvider so it can read both via their hooks.
const CanvasDataInner: React.FC<CanvasDataProviderProps> = ({ children }) => {
  const appMode = useAppMode();
  const {
    guestCredentials,
    guestAvailableUsers,
    pendingUserSelection,
    initializing,
    coursesMode,
    demoCourses,
    demoCourseDetails,
    offlineCourses,
    offlineCourseDetails,
    getActiveCredentials,
    setGuestCredentials,
    clearGuestCredentials: clearGuestCredentialsMode,
    resetGuestSelection,
    activateDemoMode,
    deactivateDemoMode,
  } = appMode;

  const { currentUser, availableUsers, switchCanvasUser, userId } = useCanvasSession();

  const courseData = useCourseData({
    pendingUserSelection,
    coursesMode,
    userId,
    getActiveCredentials,
    demoCourses,
    demoCourseDetails,
    offlineCourses,
    offlineCourseDetails,
  });

  const {
    selectCanvasUser,
    switchGuestUser,
    refreshAllData,
    clearGuestCredentials,
    clearAllCanvasData,
  } = useUserSwitching({
    coursesMode,
    guestCredentials,
    guestAvailableUsers,
    setGuestCredentials,
    clearGuestCredentialsMode,
    resetGuestSelection,
    switchCanvasUser,
    resetCourseData: courseData.resetCourseData,
    loadCourses: courseData.loadCourses,
    setLoadingCourses: courseData.setLoadingCourses,
    setLoadingCourseDetails: courseData.setLoadingCourseDetails,
    setLoadingCourseHistory: courseData.setLoadingCourseHistory,
    setError: courseData.setError,
  });

  // Validate a Canvas API token. Doesn't touch session/mode/course state,
  // so it stays a small standalone function here rather than living in
  // either hook.
  // `userIdArg` is annotated `string | null` rather than left to infer from
  // its `= null` default: the default alone would infer the type `null`,
  // which is false -- JS callers pass a real Canvas user id, and only the
  // absent case falls back to 'current'.
  const validateCanvasToken = React.useCallback(async (
    token: string,
    userIdArg: string | null = null,
    rootUrl: string
  ) => {
    try {
      return await testCanvasCredentials(token, userIdArg || 'current', rootUrl);
    } catch (err) {
      console.error('Token validation error:', err);
      // `catch` binds `unknown` under strict. `instanceof` is a real runtime
      // check, not an assertion, so `err.message` below is genuinely a
      // string. A non-Error rejection yields undefined here where the old
      // unguarded read would have produced undefined too for anything
      // without a `message`; see the report for the narrow case that differs.
      const message = err instanceof Error ? err.message : undefined;
      return { valid: false, error: message };
    }
  }, []);

  // Composed context value. currentUser/availableUsers/switchCanvasUser/
  // userId and the mode/guest fields (marked below) are deprecated
  // passthroughs from CanvasSessionContext/AppModeContext -- new/migrated
  // call sites should read those directly via useCanvasSession()/useAppMode().
  // `satisfies` (task 5.10b), not an annotation: a field added to this literal
  // and not to CanvasDataContextType (excess property), or the reverse
  // (missing property), now fails type-check instead of drifting silently.
  const value = useMemo(() => ({
    currentUser,
    availableUsers,
    switchCanvasUser,
    userId,
    courses: courseData.courses,
    courseDetails: courseData.courseDetails,
    // Union with the courses query's own fetching state and the pending-
    // user-selection transition, so consumers see "loading" for courses
    // triggered reactively by a key change, not just imperative loadCourses().
    loadingCourses: (coursesMode === 'demo' || coursesMode === 'offline')
      ? courseData.loadingCourses
      : (courseData.loadingCourses || pendingUserSelection || courseData.isFetchingCourses),
    loadingCourseDetails: courseData.loadingCourseDetails,
    loadingCourseHistory: courseData.loadingCourseHistory,
    error: courseData.error,
    /** @deprecated read this mode field from useAppMode() instead */
    isGuestMode: appMode.isGuestMode,
    /** @deprecated read this mode field from useAppMode() instead */
    isDemoMode: appMode.isDemoMode,
    /** @deprecated read this mode field from useAppMode() instead */
    isOfflineMode: appMode.isOfflineMode,
    /** @deprecated read this mode field from useAppMode() instead */
    initializing,
    /** @deprecated read this mode field from useAppMode() instead */
    pendingUserSelection,
    /** @deprecated read this mode field from useAppMode() instead */
    guestAvailableUsers,
    loadCourses: courseData.loadCourses,
    getCourse: courseData.getCourse,
    getCourseDetails: courseData.getCourseDetails,
    getCourseHistory: courseData.getCourseHistory,
    loadAllCourseDetails: courseData.loadAllCourseDetails,
    validateCanvasToken,
    /** @deprecated read this mode field from useAppMode() instead */
    setGuestCredentials,
    clearGuestCredentials,
    /** @deprecated read this mode field from useAppMode() instead */
    getActiveCredentials,
    selectCanvasUser,
    switchGuestUser,
    resetCourseData: courseData.resetCourseData,
    clearAllCanvasData,
    refreshAllData,
    clearCache: courseData.clearCache,
    /** @deprecated read this mode field from useAppMode() instead */
    activateDemoMode,
    /** @deprecated read this mode field from useAppMode() instead */
    deactivateDemoMode,
  } satisfies CanvasDataContextType), [
    currentUser,
    availableUsers,
    switchCanvasUser,
    userId,
    courseData.courses,
    courseData.courseDetails,
    courseData.loadingCourses,
    courseData.isFetchingCourses,
    courseData.loadingCourseDetails,
    courseData.loadingCourseHistory,
    courseData.error,
    coursesMode,
    appMode.isGuestMode,
    appMode.isDemoMode,
    appMode.isOfflineMode,
    initializing,
    pendingUserSelection,
    guestAvailableUsers,
    courseData.clearCache,
    courseData.loadCourses,
    courseData.getCourse,
    courseData.getCourseDetails,
    courseData.getCourseHistory,
    courseData.loadAllCourseDetails,
    validateCanvasToken,
    setGuestCredentials,
    clearGuestCredentials,
    getActiveCredentials,
    selectCanvasUser,
    switchGuestUser,
    courseData.resetCourseData,
    clearAllCanvasData,
    refreshAllData,
    activateDemoMode,
    deactivateDemoMode,
  ]);

  return (
    <CanvasDataContext.Provider value={value}>
      {children}
    </CanvasDataContext.Provider>
  );
};

export const CanvasDataProvider: React.FC<CanvasDataProviderProps> = ({ children }) => (
  <AppModeProvider>
    <CanvasSessionProvider>
      <CanvasDataInner>{children}</CanvasDataInner>
    </CanvasSessionProvider>
  </AppModeProvider>
);

// Make sure to export the provider as default
export default CanvasDataProvider;
